import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import * as d3 from 'd3';
import {autorun} from 'mobx';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';

import Play from '../../assets/images/play-icon';
import PlayCard from '../../assets/images/play-icon-card';
import Pause from '../../assets/images/pause-icon';
import PauseCard from '../../assets/images/pause-icon-card';
import Svg from '../Svg';

import {disabledDark, primary, white, yellow} from '../../themes/colors';
import {PlayButtonMode, PowerState, TrackPlayStatus} from '../../constants/enums';
import RaisedIconButton from '../iconButton/RaisedIconButton';
import AvatarIcon from '../AvatarIcon';
import './playButton.scss';
import {isTeacher} from "../../utils/userUtility";

const baseStyle = {
    width: 22,
    height: 22,
    minHeight: 22,
    minWidth: 22,
};
const svgStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
};

const tau = 2 * Math.PI;

@observer
class PlayButton extends Component {
    componentDidMount() {
        if (this.props.withProgress) {
            this.createArc();
            this.handlePlayPause(this.props.status);

            this.listener = autorun(() => {
                this.updateArc(this.props.powerState === undefined || this.props.powerState === PowerState.On ? this.props.playerColor : disabledDark);
            });
        }
    }

    componentWillUnmount() {
        if (this.listener) {
            this.listener();
        }
    }

    componentWillUpdate(nextProps) {
        if (nextProps.withProgress) {
            if (nextProps.status !== this.props.status) {
                this.handlePlayPause(nextProps.status);
            }
            if (this.props.progress !== nextProps.progress || nextProps.track.lastModified !== this.props.track.lastModified) {
                this.seekTo(nextProps.progress, nextProps.status);
            }
        }
    }

    createArc = () => {
        const width = this.props.style ? this.props.style.width || 45 : 45;
        const height = this.props.style ? this.props.style.height || 45 : 45;
        const svg = d3.select(this.svg);
        this.playArc = d3.arc().innerRadius((width - 2) / 2).outerRadius((width + 4) / 2).startAngle(0);
        const g = svg.append('g').attr('transform', `translate(${(width + 5) / 2},${(height + 5) / 2})`);
        this.pathEl = g.append('path')
            .datum({endAngle: 0})
            .attr('class', 'play-arc')
            .attr('fill', this.props.playerColor || yellow)
            .attr('d', this.playArc);
    };

    updateArc = (color) => {
        if (this.pathEl) {
            this.pathEl.attr('fill', color);
        }
    };

    handlePlayPause = (playStatus) => {
        if (playStatus === TrackPlayStatus.Playing) {
            this.playAnimation();
        } else if (playStatus === TrackPlayStatus.Paused) {
            this.pauseAnimation();
        } else {
            this.stopAnimation();
        }
    };

    playAnimation = () => {
        const {currentTime, duration, progress, startOffset, bpm, masterBpm} = this.props.track;
        const activeBPM = masterBpm || bpm;
        const playOffset = (!currentTime && startOffset > 0) ? (startOffset * 60) / activeBPM : 0;
        const animationDuration = this.props.duration || duration;
        if (!isNumber(animationDuration) || (animationDuration - currentTime) * 1000 <= 0) {
            // console.log('duration is NaN! skipping animation');
            return;
        }
        this.pathEl.transition()
            .duration((animationDuration - currentTime) * 1000)
            .delay(playOffset * 1000).ease(d3.easeLinear)
            .attrTween('d', (d) => {
                const interpolate = d3.interpolate(tau * progress, tau);
                return (t) => {
                    d.endAngle = interpolate(t);
                    return this.playArc(d);
                };
            })
            .on('end', this.handleAnimationEnd);
    };

    handleAnimationEnd = () => {
        if (this.props.status === TrackPlayStatus.Playing && this.props.track.progress < 1) {
            this.playAnimation();
        }
    };

    pauseAnimation = () => {
        this.pathEl.interrupt();
    };

    seekTo = (seekTo, status) => {
        if (!isUndefined(seekTo)) {
            this.pathEl.interrupt();
            this.pathEl.attr('d', (d) => {
                d.endAngle = tau * seekTo;
                return this.playArc(d);
            });
            if (status === TrackPlayStatus.Playing) {
                this.playAnimation();
            }
        }
    };

    stopAnimation = () => {
        this.pathEl.interrupt();
        this.pathEl.attr('d', (d) => {
            d.endAngle = 0;
            return this.playArc(d);
        });
    };

    handlePlayButtonClick = () => {
        this.props.onClick && this.props.onClick();
    };

    renderIconByStatus = (mode = TrackPlayStatus.Playing, status, playerColor, powerState, playerSkin) => {
        switch (mode) {
        case PlayButtonMode.MuteUnmute:
            return <AvatarIcon
                icon={this.props.avatar} className="avatar-md" useDefault={isTeacher()}
                style={{border: 'none', boxShadow: 'none'}}/>;
        case PlayButtonMode.PlayPause:
        default:
            switch (status) {
            case TrackPlayStatus.Playing:
                return <Svg
                    className={playerSkin ? 'pause-icon-card' : 'pause-icon'}
                    hoverColor={playerColor}
                    color={playerColor || yellow}
                    viewBox={playerSkin ? '0 0 36 36' : '0 0 45 45'}
                    rippleColor={`rgba(${white} , .0)`}>{playerSkin ? PauseCard : Pause}</Svg>;
            case TrackPlayStatus.Paused:
                return <Svg
                    className={playerSkin ? 'play-icon-card' : 'play-icon'}
                    color={playerColor || yellow} hoverColor={playerColor}
                    viewBox={playerSkin ? '0 0 36 36' : '0 0 45 45'}
                    rippleColor={`rgba(${white} , .0)`}>{playerSkin ? PlayCard : Play}</Svg>;
            default:
                return <Svg
                    className={playerSkin ? 'play-icon-card' : 'play-icon'}
                    color={playerColor || white} hoverColor={playerColor}
                    viewBox={playerSkin ? '0 0 36 36' : '0 0 45 45'}
                    rippleColor={`rgba(${white} , .0)`}>{playerSkin ? PlayCard : Play}</Svg>;
            }
        }
    };

    render() {
        const {id, style, status, withProgress, disabled, playerColor,
            powerState, mode, playerSkin, disableRipple, noBorder=false} = this.props;
        const buttonStyle = withProgress ? baseStyle : {...baseStyle, border: `thin solid ${primary}`};
        if (noBorder) {
            buttonStyle.border = null;
        }
        const width = style ? style.width || 45 : 45;
        const height = style ? style.height || 45 : 45;

        return (
            <RaisedIconButton
                id={id} style={{...buttonStyle, ...style}} disabled={disabled}
                containerClass="flexbox flex-center play-button" disableRipple={disableRipple}
                onClick={this.handlePlayButtonClick}>
                {withProgress &&
                <svg ref={svg => this.svg = svg} viewBox={`0 0 ${width + 5} ${height + 5}`} style={svgStyle}/>}
                {this.renderIconByStatus(mode, status, playerColor, powerState, playerSkin)}
            </RaisedIconButton>
        );
    }
}

PlayButton.propTypes = {
    onClick: PropTypes.func,
    status: PropTypes.number,
    id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    track: PropTypes.object,
    style: PropTypes.object,
    playerColor: PropTypes.string,
    playerSkin: PropTypes.string,
    progress: PropTypes.number,
    withProgress: PropTypes.bool,
    iconSize: PropTypes.number,
    disabled: PropTypes.bool,
    powerState: PropTypes.number,
    mode: PropTypes.string,
    duration: PropTypes.number,
    avatar: PropTypes.string,

};
export default PlayButton;
